import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import ImageUploader from "@/components/ImageUploader";
import HelpTip from "@/components/HelpTip";
import { toast } from "sonner";

const Section = ({ title, help, children }) => (
    <div className="bg-white border-2 border-black p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
            <div className="font-mono text-xs uppercase tracking-widest">{title}</div>
            {help && <HelpTip>{help}</HelpTip>}
        </div>
        <div className="space-y-4">{children}</div>
    </div>
);

const Field = ({ label, children, help }) => (
    <label className="block">
        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-1 flex items-center gap-2">
            {label}
            {help && <HelpTip>{help}</HelpTip>}
        </div>
        {children}
    </label>
);

const inputCls = "w-full border-2 border-black p-3 font-mono text-sm";

export default function AdminSiteSettings() {
    const [s, setS] = useState(null);
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(false);
    const { refresh, setSettingsLocal } = useSite();

    const load = async () => {
        const [cur, hist] = await Promise.all([api.get("/site-settings"), api.get("/admin/site-settings/history")]);
        setS(cur.data); setHistory(hist.data);
    };
    useEffect(() => { load(); }, []);

    const update = (patch) => {
        const next = { ...s, ...patch };
        setS(next);
        setSettingsLocal(next); // instant preview everywhere via SiteContext
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/site-settings", s);
            setS(data); setSettingsLocal(data);
            toast.success("Saved. Live changes applied.");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    const restore = async (snapshot_id) => {
        if (!window.confirm("Restore this version? Current settings will be snapshotted first (also undoable).")) return;
        try {
            const { data } = await api.post(`/admin/site-settings/restore/${snapshot_id}`);
            setS(data); setSettingsLocal(data);
            toast.success("Restored");
            load();
        } catch { toast.error("Restore failed"); }
    };

    if (!s) return <div className="font-mono text-sm">Loading…</div>;

    return (
        <div data-testid="admin-site-settings">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <h1 className="font-display text-4xl tracking-tighter">Site Settings</h1>
                    <HelpTip>Edit text, images, and section toggles for the public storefront. Changes preview instantly in this tab; click Save to apply for all visitors.</HelpTip>
                </div>
                <button onClick={save} disabled={saving} className="bg-black text-white px-5 py-3 font-mono text-xs uppercase brutal-shadow" data-testid="site-settings-save">
                    {saving ? "Saving…" : "Save Changes"}
                </button>
            </div>

            <Section title="Brand" help="Store name shown in the header and page titles; logo shown in header (optional); favicon shown in browser tabs.">
                <Field label="Store Name"><input value={s.store_name} onChange={e => update({ store_name: e.target.value })} className={inputCls} data-testid="ss-store-name" /></Field>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Logo" help="Shown in top-left header. Recommended: 400 × 120 PNG with transparent background. Leave empty to use the text wordmark 'CLONZO'.">
                        <ImageUploader value={s.logo_url || ""} onChange={v => update({ logo_url: v || null })} recommended="Recommended: 400 × 120 (PNG, transparent)" aspect="10/3" testId="ss-logo-upload" />
                    </Field>
                    <Field label="Favicon" help="Browser tab icon. Recommended 256 × 256 PNG.">
                        <ImageUploader value={s.favicon_url || ""} onChange={v => update({ favicon_url: v || null })} recommended="Recommended: 256 × 256 (PNG)" aspect="1/1" testId="ss-favicon-upload" />
                    </Field>
                </div>
            </Section>

            <Section title="Hero Section" help="The big banner at the top of the homepage. Image appears on the right, text on the left.">
                <Field label="Hero Image" help="Right side of the hero. Recommended: 1200 × 1500 (vertical, portrait).">
                    <ImageUploader value={s.hero_image_url || ""} onChange={v => update({ hero_image_url: v || null })} recommended="Recommended: 1200 × 1500 (4:5 portrait)" aspect="4/5" testId="ss-hero-upload" />
                </Field>
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Kicker (small label)"><input value={s.hero_kicker} onChange={e => update({ hero_kicker: e.target.value })} className={inputCls} /></Field>
                    <Field label="Title Line 1"><input value={s.hero_title_line1} onChange={e => update({ hero_title_line1: e.target.value })} className={inputCls} /></Field>
                    <Field label="Title Line 2"><input value={s.hero_title_line2} onChange={e => update({ hero_title_line2: e.target.value })} className={inputCls} /></Field>
                </div>
                <Field label="Title Line 3 (accent color)"><input value={s.hero_title_line3} onChange={e => update({ hero_title_line3: e.target.value })} className={inputCls} /></Field>
                <Field label="Subtitle"><textarea rows={2} value={s.hero_subtitle} onChange={e => update({ hero_subtitle: e.target.value })} className={inputCls} /></Field>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="CTA Button Text"><input value={s.hero_cta_text} onChange={e => update({ hero_cta_text: e.target.value })} className={inputCls} /></Field>
                    <Field label="CTA Button Link" help="Can be internal like /shop or external https://..."><input value={s.hero_cta_link} onChange={e => update({ hero_cta_link: e.target.value })} className={inputCls} /></Field>
                </div>
                <Field label="Meta Line (bottom-left of hero)"><input value={s.hero_meta} onChange={e => update({ hero_meta: e.target.value })} className={inputCls} /></Field>
            </Section>

            <Section title="Shop by Category Section" help="A grid on the homepage that automatically shows every category marked 'Show on homepage' in Categories.">
                <label className="flex items-center gap-2 font-mono text-xs uppercase">
                    <input type="checkbox" checked={!!s.categories_section_enabled} onChange={e => update({ categories_section_enabled: e.target.checked })} data-testid="ss-cat-section-enabled" />
                    Section enabled
                </label>
                <Field label="Section Title"><input value={s.categories_section_title} onChange={e => update({ categories_section_title: e.target.value })} className={inputCls} data-testid="ss-cat-section-title" /></Field>
                <Field label="Kicker (small label above title)"><input value={s.categories_section_kicker || ""} onChange={e => update({ categories_section_kicker: e.target.value })} className={inputCls} /></Field>
            </Section>

            <Section title="Bestsellers / New In Section" help="A grid of products you pin as bestsellers in the Bestsellers tab.">
                <label className="flex items-center gap-2 font-mono text-xs uppercase">
                    <input type="checkbox" checked={!!s.bestsellers_section_enabled} onChange={e => update({ bestsellers_section_enabled: e.target.checked })} />
                    Section enabled
                </label>
                <Field label="Section Title"><input value={s.bestsellers_section_title} onChange={e => update({ bestsellers_section_title: e.target.value })} className={inputCls} /></Field>
                <Field label="Kicker"><input value={s.bestsellers_section_kicker} onChange={e => update({ bestsellers_section_kicker: e.target.value })} className={inputCls} /></Field>
            </Section>

            <Section title="Manifesto Section" help="The 3-column 'Why we're different' block near the bottom of the homepage.">
                <label className="flex items-center gap-2 font-mono text-xs uppercase">
                    <input type="checkbox" checked={!!s.manifesto_enabled} onChange={e => update({ manifesto_enabled: e.target.checked })} />
                    Section enabled
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Kicker"><input value={s.manifesto_kicker} onChange={e => update({ manifesto_kicker: e.target.value })} className={inputCls} /></Field>
                    <Field label="Title"><input value={s.manifesto_title} onChange={e => update({ manifesto_title: e.target.value })} className={inputCls} /></Field>
                </div>
                {[1, 2, 3].map(n => (
                    <div key={n} className="grid md:grid-cols-[200px_1fr] gap-4">
                        <Field label={`Column ${n} Title`}><input value={s[`manifesto_col${n}_title`]} onChange={e => update({ [`manifesto_col${n}_title`]: e.target.value })} className={inputCls} /></Field>
                        <Field label={`Column ${n} Body`}><input value={s[`manifesto_col${n}_body`]} onChange={e => update({ [`manifesto_col${n}_body`]: e.target.value })} className={inputCls} /></Field>
                    </div>
                ))}
            </Section>

            <Section title="Announcement Bar" help="Thin bar at the very top of every page. Great for free-shipping offers, sale timers, or urgent messages.">
                <label className="flex items-center gap-2 font-mono text-xs uppercase">
                    <input type="checkbox" checked={!!s.announcement_enabled} onChange={e => update({ announcement_enabled: e.target.checked })} data-testid="ss-ann-enabled" />
                    Show announcement bar
                </label>
                <Field label="Text"><input value={s.announcement_text || ""} onChange={e => update({ announcement_text: e.target.value })} className={inputCls} data-testid="ss-ann-text" /></Field>
                <Field label="Link (optional, makes the bar clickable)"><input value={s.announcement_link || ""} onChange={e => update({ announcement_link: e.target.value })} className={inputCls} /></Field>
            </Section>

            <Section title="Navigation Menu" help="Links shown in the header. Reorder, rename, add or remove. Leave empty label to delete.">
                {(s.nav_menu || []).map((n, i) => (
                    <div key={i} className="grid grid-cols-[1fr_2fr_auto_auto] gap-2">
                        <input value={n.label} onChange={e => { const nm = [...s.nav_menu]; nm[i] = { ...nm[i], label: e.target.value }; update({ nav_menu: nm }); }} placeholder="Label" className={inputCls} />
                        <input value={n.link} onChange={e => { const nm = [...s.nav_menu]; nm[i] = { ...nm[i], link: e.target.value }; update({ nav_menu: nm }); }} placeholder="/shop or https://..." className={inputCls} />
                        <button type="button" onClick={() => { if (i === 0) return; const nm = [...s.nav_menu]; [nm[i-1], nm[i]] = [nm[i], nm[i-1]]; update({ nav_menu: nm }); }} className="border-2 border-black px-3">↑</button>
                        <button type="button" onClick={() => { const nm = s.nav_menu.filter((_, j) => j !== i); update({ nav_menu: nm }); }} className="border-2 border-black px-3">×</button>
                    </div>
                ))}
                <button type="button" onClick={() => update({ nav_menu: [...(s.nav_menu || []), { label: "New Link", link: "/" }] })} className="font-mono text-[10px] uppercase underline">+ Add Nav Link</button>
            </Section>

            <Section title="SEO" help="Shown in Google search results and browser tabs.">
                <Field label="Page Title (max 60 chars recommended)"><input value={s.seo_title || ""} onChange={e => update({ seo_title: e.target.value })} className={inputCls} data-testid="ss-seo-title" /></Field>
                <Field label="Meta Description (max 160 chars recommended)"><textarea rows={2} value={s.seo_description || ""} onChange={e => update({ seo_description: e.target.value })} className={inputCls} /></Field>
            </Section>

            <Section title="Bestsellers Rules" help="How products qualify as 'bestsellers' on the homepage.">
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Click threshold (min views to auto-qualify)"><input type="number" min={1} value={s.bestsellers_threshold || 20} onChange={e => update({ bestsellers_threshold: Number(e.target.value) })} className={inputCls} data-testid="ss-bs-threshold" /></Field>
                    <Field label="Max products shown"><input type="number" min={1} max={24} value={s.bestsellers_max_products || 8} onChange={e => update({ bestsellers_max_products: Number(e.target.value) })} className={inputCls} /></Field>
                </div>
            </Section>

            <Section title="Footer & Contact">
                <Field label="Footer Tagline"><textarea rows={2} value={s.footer_tagline} onChange={e => update({ footer_tagline: e.target.value })} className={inputCls} /></Field>
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Contact Email"><input value={s.contact_email} onChange={e => update({ contact_email: e.target.value })} className={inputCls} /></Field>
                    <Field label="Support/Returns Email"><input value={s.contact_support_email} onChange={e => update({ contact_support_email: e.target.value })} className={inputCls} /></Field>
                    <Field label="Location"><input value={s.contact_location} onChange={e => update({ contact_location: e.target.value })} className={inputCls} /></Field>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Instagram URL"><input value={s.social_instagram} onChange={e => update({ social_instagram: e.target.value })} className={inputCls} /></Field>
                    <Field label="Twitter / X URL"><input value={s.social_twitter} onChange={e => update({ social_twitter: e.target.value })} className={inputCls} /></Field>
                </div>
            </Section>

            {history.length > 0 && (
                <Section title={`Version History (${history.length})`} help="The 10 most recent saves. Click Restore to roll back; your current settings get snapshotted automatically so you can always undo the restore too.">
                    <div className="space-y-2">
                        {history.map(h => (
                            <div key={h.snapshot_id} className="flex items-center justify-between border-2 border-black p-3">
                                <div className="font-mono text-xs">{new Date(h.snapshot_at).toLocaleString()}</div>
                                <button onClick={() => restore(h.snapshot_id)} className="font-mono text-xs uppercase underline" data-testid={`restore-${h.snapshot_id}`}>Restore</button>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}
